import React, { FunctionComponent, useMemo, useState } from "react";
import { Tag } from "../../../interfaces/Tag";
import { Checkbox } from "@progress/kendo-react-inputs";
import { FilterBar } from "../../../generic/filterBar/FilterBar";
import { formatMessage, multiline } from "../../../../utils/helpers/LanguageHelper";
import { TruncatedText } from "../../../generic/truncatedText/TruncatedText";

export interface ConsistencyRuleEditorProps {
  tags: Tag[];
  selectedTagIds: string[];
  onChange: (ids: string[]) => void;
  withDescription?: boolean;
  withFilter?: boolean;
}

const ItemBadge: React.FC<{ tag: Tag }> = ({ tag }) => (
  <span
    style={{
      marginLeft: 8,
      background: "var(--color-blue)",
      color: "white",
      borderRadius: 8,
      padding: "2px 8px",
      fontSize: 10,
      flexShrink: 0,
    }}
  >
    {tag.objects.length} objects
  </span>
);

export const ConsistencyRuleEditor: FunctionComponent<ConsistencyRuleEditorProps> = ({
  tags,
  selectedTagIds,
  onChange,
  withDescription = true,
  withFilter = true,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredTags = useMemo(() => {
    const activeTags = tags.filter((t) => !t.isDeleted);
    if (!searchQuery) return activeTags;
    const q = searchQuery.toLowerCase();
    return activeTags.filter((t) => t.label.toLowerCase().includes(q));
  }, [tags, searchQuery]);

  const handleCheck = (tagId: string) => {
    onChange([...selectedTagIds, tagId]);
  };

  const handleUncheck = (tagId: string) => {
    onChange(selectedTagIds.filter((id) => id !== tagId));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px", overflow: "hidden", minWidth: 0 }}>
      {withDescription && (
        <div style={{ color: "var(--color-grey)", fontSize: "12px" }}>
          {formatMessage("cof.validation.info.selectalltags")}
        </div>
      )}
      {withFilter && (
        <FilterBar
          onFilter={(query) => setSearchQuery(query)}
          currentQuery={searchQuery}
          currentFilters={[]}
          itemsFound={searchQuery ? filteredTags.length : undefined}
          styles={{
            root: {
              display: "flex",
              alignItems: "center",
              width: "100%",
              padding: "4px 0 0 0",
            },
          }}
        />
      )}
      <div
        style={{
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          paddingTop: "4px",
        }}
      >
        {filteredTags.length === 0 ? (
          <div style={{ color: "var(--color-grey)", fontSize: "12px", padding: "8px 0" }}>
            {multiline(formatMessage("cof.validation.title.notagyet"))}
          </div>
        ) : (
          filteredTags.map((tag) => (
            <div
              key={tag.id}
              role="button"
              tabIndex={0}
              style={{ display: "flex", alignItems: "center", padding: "4px 4px 4px 0", cursor: "pointer" }}
              onClick={() => (selectedTagIds.includes(tag.id) ? handleUncheck(tag.id) : handleCheck(tag.id))}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (selectedTagIds.includes(tag.id)) handleUncheck(tag.id);
                  else handleCheck(tag.id);
                }
              }}
            >
              <Checkbox
                checked={selectedTagIds.includes(tag.id)}
                onChange={(e) => (e.value ? handleCheck(tag.id) : handleUncheck(tag.id))}
                style={{ marginRight: 8 }}
              />
              <div style={{ flex: 1, display: "flex", alignItems: "center", overflow: "hidden" }}>
                <TruncatedText text={tag.label} />
                <ItemBadge tag={tag} />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
